<template>
  <div>
    <v-menu
        ref="menu"
        v-model="menu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
        :nudge-left="right"
        :origin="right ? 'top right' : 'top left'"
    >
      <template v-slot:activator="{ on }">
        <div class="c-date-picker">
          <v-text-field
              ref="textField"
              :value="displayDate"
              flat
              solo
              append-icon="mdi-calendar"
              readonly
              v-bind="textFieldAttr"
              v-on="on"
          >
            <!--            @blur="date = parseDate(displayDate)"-->
          </v-text-field>
        </div>
      </template>
      <v-date-picker
          ref="picker"
          :type="type"
          v-model="datePicker"
          :first-day-of-week="1"
          no-title
          scrollable
          v-bind="datePickerAttr"
          @dblclick:date="menu = false"
          @change="menu = false"
          :locale="currentLanguage"
      >
      </v-date-picker>
    </v-menu>
    <v-tooltip
        v-if="disabledDateMessage"
        bottom
        v-model="showTooltipMessage"
        :position-x="tooltipX"
        :position-y="tooltipY"
        :max-width="200"
    >
      {{ disabledDateMessage }}
    </v-tooltip>
  </div>
</template>

<script>
import moment from 'moment';

/**
 * Component DatePicker.vue
 *
 * @module DatePicker
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.11] app/src/APP_DASHBOARD/templates/components/Parts/DatePicker.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  props: {
    value: null,
    textFieldAttr: {},
    datePickerAttr: {},
    alignRight: {
      type: Boolean,
    },
    type: {
      type: String,
      default: () => 'date',
      validator: value => ['date', 'month'].includes(value)
    },
    disabledDateMessage: {
      type: String,
      default: () => ''
    },
    hideMessageFromDate: {
      type: String,
      default: () => ''
    },
    hideMessageUntilDate: {
      type: String,
      default: () => ''
    },
    // The format function that displays the text inside the text input for the picker
    displayFormatter: {
      type: Function,
      default: dateMoment => dateMoment ? dateMoment.format('DD.MM.YYYY') : ''
    },
    // Only necessary to specify if you pass a string for date to the v-model. In this case you need to provide transformer
    // functions to convert the input string to moment and back, by default the transformer functions assume YYYY-MM-DD string format
    stringTransformer: {
      type: Object,
      default: () => {
        return {
          toMoment: string => moment(string, 'YYYY-MM-DD'),
          toDateString: dateMoment => dateMoment.format('YYYY-MM-DD')
        }
      }
    }
  },
  name: "DatePicker",
  data: vm => ({
    datePicker: null,
    dateMoment: null,
    menu: false,
    right: 0,
    showTooltipMessage: false,
    tooltipX: 0,
    tooltipY: 0,
    coordOfDisabled: null,
    modelType: 'string',
  }),
  computed: {
    currentLanguage() {
      return this.$store.getters.getCurrentLanguage
    },
    displayDate() {
      return this.dateMoment ? this.displayFormatter(this.dateMoment) : '';
    }
  },
  created() {
    this.detectDateModelType();
    this.dateMoment = this.convertModelValueToMoment(this.value, this.modelType);
  },
  watch: {
    value(value) {
      this.detectDateModelType();
      this.dateMoment = this.convertModelValueToMoment(value, this.modelType);
    },
    datePicker(datePicker) {
      this.dateMoment = this.convertDatePickerValueToMoment(datePicker, this.type);
    },
    dateMoment(dateMoment) {
      // Set value for datepicker
      this.datePicker = this.convertMomentToValueForDatePicker(dateMoment, this.type);
      
      // Handle emit
      if (!dateMoment) {
        this.$emit('input', null)
      }

      let emitDate = dateMoment;

      if (this.modelType === 'date') {
        emitDate = dateMoment.toDate();
      } else if (this.modelType === 'string') {
        emitDate = this.stringTransformer.toDateString(dateMoment);
      }

      this.$emit('input', emitDate)
    },
    menu(curr) {
      const inputWidth = this.$refs.textField.$el.clientWidth
      if (inputWidth && this.alignRight) {
        this.right = 290 - inputWidth
      }
      if (!this.disabledDateMessage) {
        return;
      }
      // if date is disabled in v-date-picker the events won't work
      // add event listeners for click and hover
      if (curr) {
        setTimeout(() => {
          const el = this.$refs.picker.$el;
          el.addEventListener('click', this.handleClickOnDisabled);
          el.addEventListener('mouseover', this.handleMouseMoveOnDisabled);
        }, 200)
      } else {
        const el = this.$refs.picker.$el;
        el.removeEventListener('click', this.handleClickOnDisabled);
        el.removeEventListener('mouseover', this.handleMouseMoveOnDisabled);
      }
    }
  },
  methods: {
    detectDateModelType() {
      if (this.value instanceof Date) {
        return this.modelType = 'date';
      }
      if (moment.isMoment(this.value)) {
        return this.modelType = 'moment';
      }

      return this.modelType = 'string';
    },
    convertModelValueToMoment(value, modelType) {
      if (!value) {
        return null;
      }else if (modelType === 'date') {
          return moment(value);
      } else if (modelType === 'string') {
          return this.stringTransformer.toMoment(value);
      }else if (modelType === 'moment') {
        return value;
      }

      throw new Error('Unknown modelType ' + this.modelType);
    },
    convertMomentToValueForDatePicker(dateMoment, type) {
      if (!dateMoment) {
        return null;
      }

      if (type === 'month') {
        return dateMoment.format('YYYY-MM');
      }

      return dateMoment.format('YYYY-MM-DD');
    },
    convertDatePickerValueToMoment(datePickerValue, type) {
      if (!datePickerValue) {
        return null;
      }

      if (type === 'month') {
        return moment(datePickerValue, "YYYY-MM");
      }

      return moment(datePickerValue);
    },
    handleClickOnDisabled(e) {
      const target = e.target;
      const date = this.getDateFromTargetClick(target)

      // show tooltip on disabled click if is in valid date range
      if (date && this.isDateValidForMessage(date)) {
        this.getTooltipCoordinates(target)
      }
    },
    handleMouseMoveOnDisabled(e) {
      const target = e.target;
      const rect = target.getBoundingClientRect();

      if (!this.showTooltipMessage || !this.coordOfDisabled || !rect) {
        return;
      }
      // hide tooltip when moving away from disabled date
      if (rect.top > this.coordOfDisabled.top ||
          rect.bottom < this.coordOfDisabled.bottom ||
          rect.right > this.coordOfDisabled.right ||
          rect.left < this.coordOfDisabled.left
      ) {
        this.showTooltipMessage = false
      }
    },
    getDateFromTargetClick(targetElement) {
      // only get date if that date is disabled
      if (!targetElement.firstElementChild?.classList.contains('v-btn--disabled') ||
          targetElement?.classList.contains('v-date-picker-header')) {
        return null;
      }

      const targetText = targetElement.innerText
      const picker = this.$refs.picker;
      let year = picker.tableYear.toString();
      let month = '';
      let date = '';

      if (picker.activePicker === 'DATE') {
        // tableMonth is zero based
        month = (picker.tableMonth + 1).toString();
        date = targetText;

        if (month.length === 1) {
          month = `0${month}`;
        }
        if (date.length === 1) {
          date = `0${date}`;
        }
      } else if (picker.activePicker === 'MONTH') {
        month = moment().locale(this.currentLanguage).month(targetText).format('MM')
      }

      return `${year}-${month}${date ? '-' + date : ''}`;
    },
    isDateValidForMessage(date) {
      const from = this.hideMessageFromDate;
      const until = this.hideMessageUntilDate;
      let valid = true;

      if (from && until) {
        valid = moment(from).isAfter(date) || moment(until).isBefore(date)
      } else if (from && !until) {
        valid = moment(from).isAfter(date)
      } else if (!from && until) {
        valid = moment(until).isBefore(date)
      }

      return valid;
    },
    getTooltipCoordinates(element) {
      const rect = element.getBoundingClientRect();
      this.tooltipX = rect.left + rect.width / 2;
      this.tooltipY = rect.bottom;
      this.showTooltipMessage = true
      this.coordOfDisabled = rect
    },
  },
};
</script>

<style lang="scss">
@import "../../../../scss/colors";

.c-date-picker {
  .v-text-field--solo > .v-input__control {
    height: 48px;
    margin-bottom: 5px;

    > .v-input__slot {
      background: map-get($coroColors, lightgray);
      border: 1px solid map-get($coroColors, lightgray2);
    }
  }
}
</style>